import { useEffect, useMemo, useState } from "react";
import type { DealTypeMatchCatalog, DealTypeMatchState } from "@/lib/opsDealTypes/dealTypeMatch";
import { OpsDealTypeMatchBadge } from "./OpsDealTypeMatchBadge";

const selectClass = "mt-1 block w-full rounded border border-gray-300 px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-900";

export function OpsDealTypeMatchCard(props: {
  match: DealTypeMatchState | null;
  catalog: DealTypeMatchCatalog | null;
  busy?: boolean;
  onAssign: (productTypeId: string, saleTypeId: string | null) => Promise<void>;
  onRematch: () => Promise<void>;
}) {
  const match = props.match;
  const catalog = props.catalog;
  const [productId, setProductId] = useState(match?.product_type_id ?? "");
  const [saleId, setSaleId] = useState(match?.sale_type_id ?? "");
  useEffect(() => {
    setProductId(match?.product_type_id ?? "");
    setSaleId(match?.sale_type_id ?? "");
  }, [match?.product_type_id, match?.sale_type_id, match?.status]);

  const products = catalog?.product_types.filter((p) => p.is_active) ?? [];
  const linkedSaleIds = useMemo(() => {
    if (!catalog || !productId) return new Set<string>();
    return new Set(catalog.links.filter((l) => l.product_type_id === productId).map((l) => l.sale_type_id));
  }, [catalog, productId]);
  const sales = (catalog?.sale_types ?? []).filter((s) => s.is_active && linkedSaleIds.has(s.id));

  return (
    <div className="h-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-950">
      <div className="flex flex-wrap items-center gap-2">
        <OpsDealTypeMatchBadge
          status={match?.status}
          productName={match?.product_type_name}
          saleName={match?.sale_type_name}
        />
        <span className="font-medium text-gray-900 dark:text-zinc-100">
          {match?.status === "matched"
            ? "Deal type matched"
            : match?.status === "product_only"
              ? "Product type matched — pick sale type"
              : "Deal type not matched"}
        </span>
        {match?.product_type_name ? (
          <span className="text-xs text-gray-500">
            {[
              match.product_type_name,
              match.sale_type_name,
              match.fulfillment === "inventory"
                ? "Inventory"
                : match.fulfillment === "custom"
                  ? "Custom"
                  : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </span>
        ) : match?.fulfillment ? (
          <span className="text-xs text-gray-500">
            {match.fulfillment === "inventory" ? "Inventory" : "Custom"}
          </span>
        ) : null}
      </div>
      {match?.reason ? <p className="mt-1 text-xs text-gray-600 dark:text-zinc-400">{match.reason}</p> : null}
      <div className="mt-2 flex flex-wrap items-end gap-2">
        <label className="block text-sm">Product type
          <select
            className={selectClass}
            value={productId}
            onChange={(e) => {
              setProductId(e.target.value);
              setSaleId("");
            }}
          >
            <option value="">Select…</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>{p.display_name}</option>
            ))}
          </select>
        </label>
        <label className="block text-sm">Sale type
          <select
            className={selectClass}
            value={saleId}
            disabled={!productId}
            onChange={(e) => setSaleId(e.target.value)}
          >
            <option value="">{productId ? "Select…" : "Pick product first"}</option>
            {sales.map((s) => (
              <option key={s.id} value={s.id}>{s.display_name}</option>
            ))}
          </select>
        </label>
        <button
          type="button"
          disabled={props.busy === true || !productId}
          className="rounded bg-indigo-600 px-3 py-1.5 text-xs text-white disabled:opacity-50"
          onClick={() => void props.onAssign(productId, saleId || null)}
        >
          Save match
        </button>
        <button
          type="button"
          disabled={props.busy === true}
          className="rounded border px-3 py-1.5 text-xs"
          onClick={() => void props.onRematch()}
        >
          Re-match from feed
        </button>
      </div>
    </div>
  );
}
